import { useState, useCallback } from 'react';
import { QueryKey } from 'react-query';

import { InvoiceProcessingJobStatus } from '../../../../../../../main/invoiceProcessingJobs/invoiceProcessingJobsTypes';

import {
  UniversalFilterInvoiceProcessingJobStatusValue,
  UniversalFilterInvoiceProcessingJobStatusModes,
  UniversalFilterInvoiceProcessingJobStatusFilterState,
  UniversalFilterInvoiceProcessingJobStatusOnFilterStateChange,
  UniversalFilterInvoiceProcessingJobStatusOnChange
} from '../../UniversalFilterInvoiceProcessingJobStatus.types';

import { useMountEffect } from '../../../../../../../common/hooks/useMountEffect';
import { useReactQueryState } from '../../../../../../../main/common/reactQuery/hooks/useReactQueryState';

import { useUniversalFilterInvoiceProcessingJobStatusFilterState } from '../useUniversalFilterInvoiceProcessingJobStatusFilterState';

import {
  UniversalFilterTemplateModeOption,
  UniversalFilterTemplateOnModeChange
} from '../../../../helpers/UniversalFilterTemplate';

import { filtersKeys } from '../../../../../../../locales/keys';

const defaultMode = UniversalFilterInvoiceProcessingJobStatusModes.IS_ANY_OF;

const modeOptions: UniversalFilterTemplateModeOption<UniversalFilterInvoiceProcessingJobStatusModes>[] =
  [
    {
      value: UniversalFilterInvoiceProcessingJobStatusModes.IS_ANY_OF,
      i18nText: filtersKeys.modes.isAnyOf
    },
    {
      value: UniversalFilterInvoiceProcessingJobStatusModes.IS_NONE_OF,
      i18nText: filtersKeys.modes.isNoneOf
    }
  ];

interface UniversalFilterInvoiceProcessingJobStatusOptions {
  filterStateCacheKey: QueryKey;
  filterValue?: UniversalFilterInvoiceProcessingJobStatusValue;
  onChange: UniversalFilterInvoiceProcessingJobStatusOnChange;
}

function useUniversalFilterInvoiceProcessingJobStatus({
  filterStateCacheKey,
  filterValue,
  onChange
}: UniversalFilterInvoiceProcessingJobStatusOptions) {
  const { value: savedState, setValue: saveState } =
    useReactQueryState<UniversalFilterInvoiceProcessingJobStatusFilterState>(
      filterStateCacheKey
    );

  const { handleApplyFilterState, handleParseFilterValue } =
    useUniversalFilterInvoiceProcessingJobStatusFilterState({
      onChange
    });

  const [filterState, setFilterState] =
    useState<UniversalFilterInvoiceProcessingJobStatusFilterState>(
      savedState ||
        handleParseFilterValue(filterValue) || {
          mode: defaultMode,
          statuses: []
        }
    );

  useMountEffect(() => {
    handleApplyFilterState(filterState);
  });

  const handleFilterStateChange =
    useCallback<UniversalFilterInvoiceProcessingJobStatusOnFilterStateChange>(
      (state) => {
        setFilterState(state);
        handleApplyFilterState(state);
        saveState(state);
      },
      [handleApplyFilterState, saveState]
    );

  const handleModeChange = useCallback<
    UniversalFilterTemplateOnModeChange<UniversalFilterInvoiceProcessingJobStatusModes>
  >(
    (mode) => {
      handleFilterStateChange({
        mode,
        statuses: filterState.statuses || []
      });
    },
    [filterState, handleFilterStateChange]
  );

  const handleStatusesChange = useCallback<
    (statuses: InvoiceProcessingJobStatus[]) => void
  >(
    (statuses) => {
      handleFilterStateChange({
        mode: filterState.mode,
        statuses: statuses || []
      });
    },
    [filterState, handleFilterStateChange]
  );

  return {
    modeOptions,
    filterState,
    handleModeChange,
    handleStatusesChange
  };
}

export default useUniversalFilterInvoiceProcessingJobStatus;
